import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import ShareIcon from '@mui/icons-material/PersonAddAlt'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import api, { apiErrorMessage } from '../api/client'
import { useProject } from '../api/hooks'
import SharedBadge from '../components/SharedBadge'
import { statusLabels, type Project, type ProjectStatus } from '../api/types'
import { formatDate } from '../format'
import { useAmountsHidden } from '../privacy'
import ResultsPanel from '../components/ResultsPanel'
import ShareDialog from '../components/ShareDialog'
import { ActionsMenu } from '../components/ActionsMenu'

const statusOptions = Object.entries(statusLabels) as [ProjectStatus, string][]

export default function ProjectDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: project, loading, error, reload } = useProject(id!)
  useAmountsHidden()
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  const [name, setName] = useState('')
  const [status, setStatus] = useState<ProjectStatus>('negotiating')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState('')
  const [saved, setSaved] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  // Sync form fields from the fetched project once, without an effect: keeps
  // track of the last project the form was seeded from and re-seeds only when
  // that identity changes (new fetch), not on every render or user edit.
  const [syncedProject, setSyncedProject] = useState<Project | null>(null)
  if (project && project !== syncedProject) {
    setSyncedProject(project)
    setName(project.name)
    setStatus(project.status)
    setNotes(project.notes)
  }

  if (loading) return <CircularProgress />
  if (error) return <Alert severity="error">{error}</Alert>
  if (!project) return null

  const readOnly = project.role === 'reader'
  const isOwner = project.isOwner

  const save = async () => {
    setSaving(true)
    setActionError('')
    setSaved(false)
    try {
      await api.put<Project>(`/projects/${project.id}`, { name, status, notes })
      setSaved(true)
      reload()
    } catch (err) {
      setActionError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!window.confirm('Supprimer ce projet ?')) return
    try {
      await api.delete(`/projects/${project.id}`)
      navigate('/projects')
    } catch (err) {
      setActionError(apiErrorMessage(err))
    }
  }

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography variant="h4">{project.name}</Typography>
            <SharedBadge
              role={project.role}
              ownerEmail={project.ownerEmail}
              sharedWithOthers={project.sharedWithOthers}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Créé le {formatDate(project.createdAt)}
          </Typography>
          {!isOwner && project.ownerEmail && (
            <Typography variant="body2" sx={{ color: 'info.main', overflowWrap: 'anywhere' }}>
              Partagé par {project.ownerEmail}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {isOwner && (
            <>
              {isMobileView ? (
                <ActionsMenu
                  items={[
                    { label: 'Partager', Icon: ShareIcon, onClick: () => setShareOpen(true) },
                    { label: 'Supprimer', Icon: DeleteIcon, color: 'error', onClick: remove },
                  ]}
                />
              ) : (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareOpen(true)}
                  >
                    Partager
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={remove}
                  >
                    Supprimer
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>

      {isOwner && (
        <ShareDialog projectId={project.id} open={shareOpen} onClose={() => setShareOpen(false)} />
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Projet enregistré.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Fiche projet
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={readOnly}
              />
              <TextField
                select
                label="Statut"
                value={status}
                onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                disabled={readOnly}
              >
                {statusOptions.map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                minRows={4}
                disabled={readOnly}
              />
              {!readOnly && (
                <Button variant="contained" onClick={save} disabled={saving || !name.trim()}>
                  Enregistrer
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Simulation d'origine
            </Typography>
            {project.simulation ? (
              <>
                <Button
                  component={RouterLink}
                  to={`/simulations/${project.simulation.id}`}
                  variant="text"
                  sx={{ px: 0, mb: 1 }}
                >
                  {project.simulation.name}
                </Button>
                <Divider sx={{ mb: 2 }} />
                <ResultsPanel r={project.simulation.results} compact />
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Ce projet n'est lié à aucune simulation.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}
